<template>
    <div class="email-broadcast">
        <a-steps :current="currentStep - 1" @change="handleStepChange">
            <a-step :title="$t('message.titleAndDesc')" />
            <a-step :title="$t('message.templateAndMailSelection')" />
            <a-step :title="$t('message.peopleToSend')" />
            <a-step :title="$t('message.submissionPlans')" />
            <a-step :title="$t('message.summary')" />
        </a-steps>

        <div class="steps-content">
            <div v-if="currentStep === 1">
                <a-form-item :label="$t('message.broadcastTitle')">
                    <a-input v-model:value="broadcast.title" />
                </a-form-item>
                <a-form-item :label="$t('message.description')">
                    <a-textarea v-model:value="broadcast.description" />
                </a-form-item>
            </div>
            <div v-if="currentStep === 2" class="template-selection">
                <div class="selection-panel">
                    <a-form-item :label="$t('message.sentByEmail')">
                        <a-select v-model:value="broadcast.senderEmail">
                            <a-select-option v-for="email in mails" :key="email._id" :value="email._id">
                                {{ email.mail }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>
                    <a-form-item :label="$t('message.emailTemplate')">
                        <a-select v-model:value="broadcast.template">
                            <a-select-option v-for="temp in templates" :key="temp.key" :value="temp.key">
                                {{ temp.name }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>
                    <div v-if="broadcast.template">
                        <a-form-item v-for="param in getParams()" :key="param" :label="param">
                            <a-select v-model="selectedParam[param]" @change="handleParamChange(param, $event)">
                                <a-select-option v-for="option in getOptions()" :key="option.value"
                                    :value="option.value">
                                    {{ option.label }}
                                </a-select-option>
                            </a-select>
                            <a-input v-if="selectedParam[param] === 'custom'" :value="getParamVal(param)"
                                @input="updateParam(param, 'custom', $event.target.value)" />
                        </a-form-item>
                    </div>
                </div>

                <div class="preview-panel">
                    <EmailTemplateEditor v-if="broadcast.template" :template="selectedTemplate" preview-mode
                        :key="broadcast.template" />
                </div>
            </div>
            <div v-if="currentStep === 3" class="members-content" style="display: flex; gap: 10px">
                <a-alert :message="$t('message.onlyPeopleWithEmail')" type="info" />
                <a-select v-model:value="broadcast.customerGroup">
                    <a-select-option v-for="customerGroup in customerGroups" :key="customerGroup._id"
                        :value="customerGroup._id">
                        {{ customerGroup.name }}
                    </a-select-option>
                </a-select>
                <div class="members-content-list">
                    <div class="members-content-title"> {{ $t("wp.customerGroups") }} ({{ totalContacts }})</div>
                    <supsis-table :columns="getColumns()" :rows="contacts" v-if="broadcast.customerGroup" />
                </div>
            </div>

            <div v-if="currentStep === 4" class="schedule-step">
                <supsis-button :primary="isNowSelected" :ghost="!isNowSelected" @click="scheduleNow"
                    classNames="schedule-button">
                    {{ $t("message.sendNow") }}
                </supsis-button>
                <supsis-button :primary="isScheduleSelected" :ghost="!isScheduleSelected" @click="scheduleLater"
                    classNames="schedule-button">
                    {{ $t("message.plan") }}
                </supsis-button>
                <div v-if="isScheduleSelected">
                    <a-date-picker v-model="scheduleDate" show-time />
                </div>
            </div>

            <div v-if="currentStep === 5" class="summary-step">
                <div class="summary-boxes">
                    <div class="summary-box">
                        <div class="box-title">{{ $t("message.title") }}</div>
                        <div class="box-value">{{ broadcast.title }}</div>
                    </div>
                    <div class="summary-box">
                        <div class="box-title">{{ $t("message.senderEmail") }}</div>
                        <div class="box-value">{{ getSenderEmail }}</div>
                    </div>
                    <div class="summary-box">
                        <div class="box-title">{{ $t("message.totalBuyer") }}</div>
                        <div class="box-value">{{ totalContacts }} {{ $t("message.person") }}</div>
                    </div>
                    <div class="summary-box">
                        <div class="box-title">{{ $t("message.estimatedDuration") }}</div>
                        <div class="box-value">{{ estimatedDuration }}</div>
                    </div>
                </div>
            </div>
            <div v-if="currentStep === 6" class="final-step">
                <p>{{ $t("message.shippingIsNowComplete") }}</p>
            </div>
        </div>

        <div class="steps-action">
            <div class="button-group">
                <supsis-button v-if="currentStep === 1" @click="gotoList" small> {{ $t("message.back")
                }}</supsis-button>
                <supsis-button v-if="currentStep > 1" @click="prev" small> {{ $t("message.back") }}</supsis-button>
                <supsis-button v-if="currentStep < 5" primary small @click="next"> {{ $t("message.continue")
                }}</supsis-button>
                <supsis-button v-if="currentStep === 5" primary small @click="submitBroadcast"> {{ $t("message.send")
                }}</supsis-button>
            </div>
        </div>
    </div>
</template>

<script>
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import EmailTemplateEditor from "../templates/EmailTemplateEditor.vue";
import { MAIL_API } from "../../../../api/mail";
import { mapState } from "vuex";
import SupsisTable from "../../../../components/SupsisTable.vue";

export default {
    name: "EmailBroadcast",
    components: {
        SupsisTable,
        SupsisButton,
        EmailTemplateEditor,
    },
    data() {
        return {
            currentStep: 1,
            broadcast: {
                title: "",
                description: "",
                template: undefined,
                senderEmail: undefined,
                customerGroup: undefined,
            },
            templates: [],
            templateDesigns: {},
            contacts: [],
            customerGroups: [],
            totalContacts: 0,
            params: [],
            selectedTemplate: null,
            fields: [
                {
                    field: "fullname",
                    text: this.$t("message.fullName"),
                },
                {
                    field: "email",
                    text: this.$t("message.email"),
                },
                {
                    field: "phone",
                    text: this.$t("message.phone"),
                },
                {
                    field: "city",
                    text: this.$t("message.city"),
                },
                {
                    field: "country",
                    text: this.$t("message.country"),
                },
            ],
            scheduleDate: null,
            isNowSelected: true,
            isScheduleSelected: false,
            selectedParam: {},
            parameterOptions: [
                { value: "custom", label: this.$t("message.customValue") },
                { value: "fullname", label: this.$t("message.customerName") },
                { value: "email", label: this.$t("message.customerEmail") },
                { value: "phone", label: "Numara" },
            ],
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            mails: (state) => state.mail.allMailAddresses,
            contactProperties: (state) => state.contactProperty.data,
        }),
        getSenderEmail() {
            const selectedMail = this.mails.find((mail) => mail._id === this.broadcast.senderEmail);
            return selectedMail ? selectedMail.mail : "-";
        },
        estimatedDuration() {
            const totalSeconds = this.totalContacts / 3;
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = Math.ceil(totalSeconds % 60);

            const parts = [];
            if (hours > 0) parts.push(`${hours} saat`);
            if (minutes > 0) parts.push(`${minutes} dakika`);
            if (seconds > 0) parts.push(`${seconds} saniye`);

            return parts.join(" ") || "0 saniye";
        },
    },
    methods: {
        getOptions() {
            return [
                ...this.parameterOptions,
                ...this.contactProperties.map((property) => ({
                    value: property.name,
                    label: property.label,
                })),
            ];
        },
        gotoList() {
            this.$emit("close");
        },
        handleStepChange(current) {
            this.currentStep = current + 1;
        },
        next() {
            this.currentStep++;
        },
        prev() {
            this.currentStep--;
        },
        async getSelectedTemplate() {
            this.selectedTemplate = await MAIL_API.TEMPLATE.getEmailTemplateById(this.broadcast.template);
        },
        submitBroadcast() {
            const { title, description, template, senderEmail, customerGroup } = this.broadcast;
            if (!template) {
                this.$message.error(this.$t("message.selectMailTemplate"));
                return;
            }
            if (!senderEmail) {
                this.$message.error(this.$t("message.youMustSelectSendingMailAddress"));
                return;
            }
            if (!customerGroup) {
                this.$message.error(this.$t("message.youMustSelectCustomerGroupSend"));
                return;
            }
            const scheduleAt = this.isScheduleSelected ? this.scheduleDate.toString() : null;
            this.$emit("broadcast-created", {
                title,
                description,
                senderEmail,
                customerGroup,
                template: this.templateDesigns[template]._id,
                params: this.params,
                scheduleAt,
            });
        },
        fetchCustomerGroups() {
            MAIL_API.BROADCAST.getCustomerGroups().then((res) => {
                this.customerGroups = res.data;
            });
        },
        getCustomerGroupContacts(groupId) {
            MAIL_API.BROADCAST.getCustomerGroupContacts(groupId, 1, 20).then((res) => {
                const { visitors, total } = res.data;
                this.contacts = visitors;
                this.totalContacts = total;
            });
        },
        async fetchTemplates() {
            try {
                const { templates } = await MAIL_API.TEMPLATE.listEmailTemplates({
                    filters: [],
                    page: 1,
                    perPage: 100,
                });

                this.templates = templates.map((t) => ({ key: t._id, name: t.name }));
                this.templateDesigns = templates.reduce((acc, template) => {
                    acc[template._id] = template;
                    return acc;
                }, {});
            } catch (error) {
                console.error("Error fetching templates:", error);
            }
        },
        getColumns() {
            return [
                {
                    field: "fullname",
                    key: "fullname",
                    title: this.$t("message.fullName"),
                    width: 150,
                },
                {
                    field: "email",
                    key: "email",
                    title: this.$t("message.email"),
                    width: 200,
                },
                {
                    field: "phone",
                    key: "phone",
                    title: this.$t("message.phone"),
                    width: 150,
                },
            ];
        },
        getParams() {
            const template = this.selectedTemplate;
            const html = template.html || "";
            const params = html.match(/{{(.*?)}}/g) || [];
            return params.map((param) => param.replace(/{{|}}/g, ""));
        },
        updateParam(param, key, value) {
            this.params = this.params.filter((p) => p.param !== param);
            this.params.push({ param, key, value });
        },
        getParamVal(param) {
            return this.params.find((p) => p.param === param)?.value;
        },
        scheduleNow() {
            this.isNowSelected = true;
            this.isScheduleSelected = false;
        },
        scheduleLater() {
            this.isNowSelected = false;
            this.isScheduleSelected = true;
        },
        handleParamChange(param, key) {
            this.selectedParam[param] = key;
            this.updateParam(param, key);
        },
    },
    mounted() {
        this.fetchCustomerGroups();
        this.fetchTemplates();
    },
    watch: {
        "broadcast.customerGroup": {
            handler(groupId) {
                if (this.broadcast.customerGroup) this.getCustomerGroupContacts(groupId);
            },
            deep: true,
        },
        "broadcast.template": {
            handler() {
                if (this.broadcast.template) this.getSelectedTemplate();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.email-broadcast {
    padding: 24px;
    background: #fff;

    .steps-content {
        margin-top: 24px;
        padding: 24px;
        background: #fafafa;
        border: 1px solid #e9e9e9;
        border-radius: 2px;
        min-height: 200px;
    }

    .steps-action {
        margin-top: 24px;
        display: flex;
        justify-content: flex-end;

        .button-group {
            display: flex;
            gap: 8px;
            align-items: center;
            margin-left: auto;
        }
    }

    .ant-steps-item {
        cursor: pointer;
    }

    .template-selection {
        display: grid;
        grid-template-columns: 300px 1fr;
        gap: 24px;

        .selection-panel {
            padding: 16px;
            background: #fff;
            border-radius: 4px;

            .ant-select {
                width: 100%;
            }
        }

        .preview-panel {
            border: 1px solid #e9e9e9;
            border-radius: 4px;
            overflow: hidden;
        }
    }

    .members-content {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .ant-select {
            width: 100%;
        }

        &-list {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-top: 20px;

            .members-content-title {
                font-size: 16px;
                font-weight: 500;
                color: #22272d;
            }
        }
    }

    .summary-step {
        .summary-boxes {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 16px;

            .summary-box {
                background: #fff;
                padding: 16px;
                border-radius: 8px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

                .box-title {
                    font-weight: 600;
                    color: #666;
                    margin-bottom: 8px;
                    font-size: 14px;
                }

                .box-value {
                    font-size: 16px;
                    color: #22272d;
                }
            }
        }
    }
}

.schedule-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.schedule-button {
    width: 300px !important;
    min-width: 300px !important;
}
</style>
